import { Injectable } from '@angular/core'
import { NavigationEnd, Router, RouterEvent } from '@angular/router'
import { Store } from '@ngrx/store'
import { UpdateUserAttributes, User } from '@tokeet/tv3-platform'
import { filter } from 'rxjs/operators'
import * as lodash from 'lodash'

export interface AcmSupaDemo {
  title: string
  id: string
  urlPath: string
  embed: string
}

export const acmSupademos: AcmSupaDemo[] = [
  {
    title: 'Calendar in AdvanceCM',
    id: 'cm7th9twq20mwhilg3l2afyfz',
    urlPath: '/calendar/multi',
    embed: `<div style="position: relative; box-sizing: content-box; max-height: 80vh; max-height: 80svh; width: 100%; aspect-ratio: 2.203125; "><iframe src="https://app.supademo.com/embed/cm7th9twq20mwhilg3l2afyfz?embed_v=2" loading="lazy" title="Calendar in AdvanceCM" allow="clipboard-write;autoplay" frameborder="0" webkitallowfullscreen="true" mozallowfullscreen="true" allowfullscreen style="position: absolute; top: 0; left: 0; width: 100%; height: 100%;"></iframe></div>`,
  },
  {
    title: 'Rates in AdvanceCM',
    id: 'cm7tghnn41zokhilg71b20spl',
    urlPath: '/rates',
    embed: `<div style="position: relative; box-sizing: content-box; max-height: 80vh; max-height: 80svh; width: 100%; aspect-ratio: 2.212173913043478; "><iframe src="https://app.supademo.com/embed/cm7tghnn41zokhilg71b20spl?embed_v=2" loading="lazy" title="Rates in AdvanceCM" allow="clipboard-write;autoplay" frameborder="0" webkitallowfullscreen="true" mozallowfullscreen="true" allowfullscreen style="position: absolute; top: 0; left: 0; width: 100%; height: 100%;"></iframe></div>`,
  },
  {
    title: 'Autopilot Wizard (ACM)',
    id: 'cm7t3l8261nv2hilg00v98yxw',
    urlPath: '/automations',
    embed: `<div style="position: relative; box-sizing: content-box; max-height: 80vh; max-height: 80svh; width: 100%; aspect-ratio: 1.7294685990338163; "><iframe src="https://app.supademo.com/embed/cm7t3l8261nv2hilg00v98yxw?embed_v=2" loading="lazy" title="Autopilot Wizard (ACM)" allow="clipboard-write;autoplay" frameborder="0" webkitallowfullscreen="true" mozallowfullscreen="true" allowfullscreen style="position: absolute; top: 0; left: 0; width: 100%; height: 100%;"></iframe></div>`,
  },
  {
    title: 'Smart Devices on ACM',
    id: 'cm7t5murn1q5qhilg9kirevpg',
    urlPath: '/guests',
    embed: `<div style="position: relative; box-sizing: content-box; max-height: 75vh; max-height: 75svh; width: 100%; aspect-ratio: 1.510957324106113; "><iframe src="https://app.supademo.com/embed/cm7t5murn1q5qhilg9kirevpg?embed_v=2" loading="lazy" title="Smart Devices on ACM" allow="clipboard-write;autoplay" frameborder="0" webkitallowfullscreen="true" mozallowfullscreen="true" allowfullscreen style="position: absolute; top: 0; left: 0; width: 100%; height: 100%;"></iframe></div>`,
  },
  {
    title: 'Operations (Tasks) in ACM',
    id: 'cm7tie4qc2224hilg16ka2ql9',
    urlPath: '/tasks',
    embed: `<div style="position: relative; box-sizing: content-box; max-height: 80vh; max-height: 80svh; width: 100%; aspect-ratio: 2.203125; "><iframe src="https://app.supademo.com/embed/cm7tie4qc2224hilg16ka2ql9?embed_v=2" loading="lazy" title="Operations (Tasks) in ACM" allow="clipboard-write;autoplay" frameborder="0" webkitallowfullscreen="true" mozallowfullscreen="true" allowfullscreen style="position: absolute; top: 0; left: 0; width: 100%; height: 100%;"></iframe></div>`,
  },
  {
    title: 'Documents in AdvanceCM',
    id: 'cm7t4qnip1p47hilgofperjh6',
    urlPath: '/templates',
    embed: `<div style="position: relative; box-sizing: content-box; max-height: 75vh; max-height: 75svh; width: 100%; aspect-ratio: 1.510957324106113; "><iframe src="https://app.supademo.com/embed/cm7t4qnip1p47hilgofperjh6?embed_v=2" loading="lazy" title="Documents in AdvanceCM" allow="clipboard-write;autoplay" frameborder="0" webkitallowfullscreen="true" mozallowfullscreen="true" allowfullscreen style="position: absolute; top: 0; left: 0; width: 100%; height: 100%;"></iframe></div>`,
  },
  {
    title: 'Documents in AdvanceCM',
    id: 'cm7t4qnip1p47hilgofperjh6',
    urlPath: '/inquiries/inbox',
    embed: `<div style="position: relative; box-sizing: content-box; max-height: 75vh; max-height: 75svh; width: 100%; aspect-ratio: 1.510957324106113; "><iframe src="https://app.supademo.com/embed/cm7t4qnip1p47hilgofperjh6?embed_v=2" loading="lazy" title="Documents in AdvanceCM" allow="clipboard-write;autoplay" frameborder="0" webkitallowfullscreen="true" mozallowfullscreen="true" allowfullscreen style="position: absolute; top: 0; left: 0; width: 100%; height: 100%;"></iframe></div>`,
  },
]

@Injectable({
  providedIn: 'root',
})
export class AcmSupaDemoService {
  pageNavigationEnd$ = this.router.events.pipe(filter((event: RouterEvent) => event instanceof NavigationEnd))

  get currentPath() {
    return this.router.url
  }

  constructor(private router: Router, private store: Store<any>) {}

  getDemoForCurrentPage() {
    return acmSupademos.find((demo) => this.currentPath.startsWith(demo.urlPath))
  }

  getFirstDemo() {
    return acmSupademos[0]
  }

  isDemoViewed(user: User, demo: AcmSupaDemo) {
    const viewedPaths: string[] = user?.attributes?.acm_demo_viewed || []
    return !!viewedPaths?.find((p) => p.startsWith(demo.urlPath))
  }

  viewDemo(user: User, demo: AcmSupaDemo) {
    const viewedPaths = user?.attributes?.acm_demo_viewed || []
    this.store.dispatch(
      UpdateUserAttributes({
        userId: user.id,
        attributes: { acm_demo_viewed: lodash.uniq([...viewedPaths, demo.urlPath]) },
        silent: true,
      })
    )
  }
}
